import React from 'react';
import { Link } from 'react-router-dom';
import { Bitcoin, TrendingUp, Shield, Database, ArrowRight, Wallet, PieChart, ExternalLink } from 'lucide-react';
import { StatsCard } from '../components/StatsCard';
import { DistributionChart } from '../components/DistributionChart';
import { getTotalTop100Balance, getTop100Percentage, CIRCULATING_SUPPLY, BTC_PRICE_USD, TOP_100_WALLETS } from '../data/wallets';
import { formatBTC, formatUSD, formatPercent } from '../utils/formatters';
export function HomePage() {
  const top100Total = getTotalTop100Balance();
  const top100Percentage = getTop100Percentage();
  const top10Total = TOP_100_WALLETS.slice(0, 10).reduce((sum, w) => sum + w.balance, 0);
  return <div className="min-h-screen bg-gray-950" data-id="element-133">
      {/* Hero Section */}
      <section className="relative overflow-hidden" data-id="element-134">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-transparent" data-id="element-135"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32" data-id="element-136">
          <div className="max-w-3xl" data-id="element-137">
            <div className="flex items-center gap-2 mb-6" data-id="element-138">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center" data-id="element-139">
                <Bitcoin className="w-7 h-7 text-white" data-id="element-140" />
              </div>
              <span className="text-orange-500 font-mono font-bold" data-id="element-141">
                TOP100BITCOIN.COM
              </span>
            </div>
            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-142">
              Track the World's
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600" data-id="element-143">
                {' '}
                Largest{' '}
              </span>
              Bitcoin Wallets
            </h1>
            <p className="text-xl text-gray-400 mb-8 leading-relaxed" data-id="element-144">
              A transparent, data-driven resource tracking the top 100 Bitcoin
              addresses. Explore whale movements, exchange reserves, and Bitcoin
              wealth distribution—all sourced from public blockchain data.
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-145">
              <Link to="/top100" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium transition-colors" data-id="element-146">
                View Top 100
                <ArrowRight className="w-5 h-5" data-id="element-147" />
              </Link>
              <Link to="/methodology" className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors border border-gray-700" data-id="element-148">
                Methodology
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-149">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-150">
          <StatsCard icon={Wallet} label="Top 100 Total" value={`${formatBTC(top100Total)} BTC`} subValue={formatUSD(top100Total * BTC_PRICE_USD)} data-id="element-151" />
          <StatsCard icon={PieChart} label="% of Circulating Supply" value={formatPercent(top100Percentage)} subValue={`${formatBTC(CIRCULATING_SUPPLY)} BTC total`} data-id="element-152" />
          <StatsCard icon={TrendingUp} label="Top 10 Concentration" value={formatPercent(top10Total / CIRCULATING_SUPPLY * 100)} subValue={`${formatBTC(top10Total)} BTC`} data-id="element-153" />
          <StatsCard icon={Database} label="Bitcoin Price" value={formatUSD(BTC_PRICE_USD)} subValue="Market data" data-id="element-154" />
        </div>
      </section>

      {/* Distribution Chart */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-155">
        <DistributionChart data-id="element-156" />
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-157">
        <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-158">
          Why Track Bitcoin Wallets?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-159">
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6" data-id="element-160">
            <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4" data-id="element-161">
              <TrendingUp className="w-6 h-6 text-blue-500" data-id="element-162" />
            </div>
            <h3 className="text-white text-xl font-bold mb-3" data-id="element-163">
              Market Intelligence
            </h3>
            <p className="text-gray-400 leading-relaxed" data-id="element-164">
              Understand Bitcoin wealth distribution and track whale movements
              that can impact market dynamics.
            </p>
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6" data-id="element-165">
            <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4" data-id="element-166">
              <Shield className="w-6 h-6 text-purple-500" data-id="element-167" />
            </div>
            <h3 className="text-white text-xl font-bold mb-3" data-id="element-168">Transparency</h3>
            <p className="text-gray-400 leading-relaxed" data-id="element-169">
              All data is sourced from public blockchain explorers. Every wallet
              links to multiple verification sources.
            </p>
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6" data-id="element-170">
            <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4" data-id="element-171">
              <Database className="w-6 h-6 text-green-500" data-id="element-172" />
            </div>
            <h3 className="text-white text-xl font-bold mb-3" data-id="element-173">Research Tool</h3>
            <p className="text-gray-400 leading-relaxed" data-id="element-174">
              Perfect for researchers, journalists, and enthusiasts studying
              Bitcoin adoption and concentration.
            </p>
          </div>
        </div>
      </section>

      {/* Top 10 Preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-175">
        <div className="flex items-center justify-between mb-8" data-id="element-176">
          <h2 className="text-3xl font-bold text-white" data-id="element-177">Top 10 Wallets</h2>
          <Link to="/top100" className="text-orange-500 hover:text-orange-400 font-medium flex items-center gap-2 transition-colors" data-id="element-178">
            View All 100
            <ArrowRight className="w-5 h-5" data-id="element-179" />
          </Link>
        </div>
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden" data-id="element-180">
          <div className="overflow-x-auto" data-id="element-181">
            <table className="w-full" data-id="element-182">
              <thead className="bg-gray-900/50 border-b border-gray-700" data-id="element-183">
                <tr data-id="element-184">
                  <th className="px-6 py-4 text-left text-gray-400 font-medium text-sm" data-id="element-185">
                    Rank
                  </th>
                  <th className="px-6 py-4 text-left text-gray-400 font-medium text-sm" data-id="element-186">
                    Label
                  </th>
                  <th className="px-6 py-4 text-right text-gray-400 font-medium text-sm" data-id="element-187">
                    Balance (BTC)
                  </th>
                  <th className="px-6 py-4 text-right text-gray-400 font-medium text-sm" data-id="element-188">
                    % Supply
                  </th>
                  <th className="px-6 py-4 text-center text-gray-400 font-medium text-sm" data-id="element-189">
                    View
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700" data-id="element-190">
                {TOP_100_WALLETS.slice(0, 10).map(wallet => <tr key={wallet.address} className="hover:bg-gray-800/50 transition-colors" data-id="element-191">
                    <td className="px-6 py-4" data-id="element-192">
                      <span className="text-white font-bold font-mono" data-id="element-193">
                        #{wallet.rank}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-194">
                      <div className="flex flex-col gap-1" data-id="element-195">
                        {wallet.label && <span className="text-white text-sm font-medium" data-id="element-196">
                            {wallet.label}
                          </span>}
                        {wallet.entity && <span className="text-gray-400 text-xs" data-id="element-197">
                            {wallet.entity}
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-198">
                      <span className="text-white font-mono font-medium" data-id="element-199">
                        {formatBTC(wallet.balance)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-200">
                      <span className="text-orange-500 font-mono font-medium" data-id="element-201">
                        {formatPercent(wallet.percentOfSupply)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center" data-id="element-202">
                      <a href={`https://www.blockchain.com/explorer/addresses/btc/${wallet.address}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-500 transition-colors inline-flex items-center gap-1" data-id="element-203">
                        <ExternalLink className="w-4 h-4" data-id="element-204" />
                      </a>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-205">
        <div className="bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-2xl p-12 text-center" data-id="element-206">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-207">
            Ready to Explore Bitcoin's Wealth Distribution?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto" data-id="element-208">
            Dive into the complete list of top 100 Bitcoin wallets with detailed
            analytics, sources, and blockchain explorer links.
          </p>
          <Link to="/top100" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-medium transition-colors text-lg" data-id="element-209">
            View Top 100 Wallets
            <ArrowRight className="w-5 h-5" data-id="element-210" />
          </Link>
        </div>
      </section>
    </div>;
}